import { Injectable, signal } from '@angular/core';

const INSTALL_DISMISSED_COUNT_KEY = 'nexus_install_dismissed_count';
const MAX_DISMISS_BEFORE_STOP = 3;

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  private deferredPrompt: any = null;
  readonly canInstall = signal(false);
  readonly isInstalled = signal(false);
  readonly shouldShowInstallDialog = signal(false);
  readonly isIosSafari = signal(false);

  initialize(): void {
    this.detectInstalled();
    this.detectIosSafari();
    this.listenForInstallPrompt();
    this.listenForAppInstalled();
  }

  private detectInstalled(): void {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;
    this.isInstalled.set(isStandalone);
  }

  private detectIosSafari(): void {
    const ua = navigator.userAgent.toLowerCase();
    const isIos = /iphone|ipad|ipod/.test(ua);
    const isSafari = /safari/i.test(ua) && !/chrome|crios|fxios/i.test(ua);
    const isStandalone = (window.navigator as any).standalone === true;

    this.isIosSafari.set(isIos && isSafari && !isStandalone);
  }

  private listenForInstallPrompt(): void {
    window.addEventListener('beforeinstallprompt', (event: Event) => {
      event.preventDefault();
      this.deferredPrompt = event;
      this.canInstall.set(true);
      this.evaluateShowDialog();
    });
  }

  private listenForAppInstalled(): void {
    window.addEventListener('appinstalled', () => {
      this.canInstall.set(false);
      this.isInstalled.set(true);
      this.shouldShowInstallDialog.set(false);
      this.deferredPrompt = null;
    });
  }

  evaluateShowDialog(): void {
    if (this.isInstalled()) return;
    if (!this.canInstall() && !this.isIosSafari()) return;

    const dismissCount = parseInt(
      localStorage.getItem(INSTALL_DISMISSED_COUNT_KEY) ?? '0', 10
    );
    if (dismissCount >= MAX_DISMISS_BEFORE_STOP) return;

    this.shouldShowInstallDialog.set(true);
  }

  async promptInstall(): Promise<boolean> {
    if (!this.deferredPrompt) return false;

    this.deferredPrompt.prompt();
    const result = await this.deferredPrompt.userChoice;
    this.deferredPrompt = null;
    this.canInstall.set(false);
    this.shouldShowInstallDialog.set(false);

    return result.outcome === 'accepted';
  }

  dismissInstallDialog(): void {
    this.shouldShowInstallDialog.set(false);
    const count = parseInt(
      localStorage.getItem(INSTALL_DISMISSED_COUNT_KEY) ?? '0', 10
    );
    localStorage.setItem(INSTALL_DISMISSED_COUNT_KEY, String(count + 1));
  }
}
